import { buildFetchConfig } from './fetchUtils';
import type {
  ChatMessage,
  ContentBlock,
  ReasoningStep,
  ToolInChecklist,
  Conversation,
  ToolInvocationData,
  ConversationMessagesResponse,
} from '@/types/chat';

/**
 * Fetch chat history for the current user
 */
export const fetchChatHistory = async (): Promise<Conversation[]> => {
  const { baseURL, headers } = buildFetchConfig();

  try {
    const response = await fetch(`${baseURL}/user/chat_history`, { headers });
    if (response.ok) {
      const data = await response.json();
      return data.conversations || [];
    }
    console.error('Failed to fetch chat history');
    return [];
  } catch (err) {
    console.error('Error fetching chat history:', err);
    return [];
  }
};

/**
 * Delete a single conversation by ID
 * @returns true if successful, false otherwise
 */
export const deleteChatConversation = async (
  conversationId: string,
): Promise<boolean> => {
  const { baseURL, headers } = buildFetchConfig();

  try {
    const response = await fetch(
      `${baseURL}/user/chat_history/${conversationId}`,
      {
        method: 'DELETE',
        headers,
      },
    );

    if (response.ok) {
      return true;
    }
    console.error('Failed to delete conversation');
    return false;
  } catch (err) {
    console.error('Error deleting conversation:', err);
    return false;
  }
};

/**
 * Clear all chat history for the current user
 * @returns true if successful, false otherwise
 */
export const clearAllChatHistory = async (): Promise<boolean> => {
  const { baseURL, headers } = buildFetchConfig();

  try {
    const response = await fetch(`${baseURL}/user/chat_history`, {
      method: 'DELETE',
      headers,
    });

    if (response.ok) {
      return true;
    }
    console.error('Failed to clear chat history');
    return false;
  } catch (err) {
    console.error('Error clearing chat history:', err);
    return false;
  }
};

/**
 * Fetch all messages for a specific conversation
 */
export const fetchConversationMessages = async (
  conversationId: string,
): Promise<ConversationMessagesResponse | null> => {
  const { baseURL, headers } = buildFetchConfig();

  try {
    const response = await fetch(
      `${baseURL}/user/chat_history/${conversationId}/messages`,
      { headers },
    );

    if (response.ok) {
      return await response.json();
    }
    console.error('Failed to fetch conversation messages');
    return null;
  } catch (err) {
    console.error('Error fetching conversation messages:', err);
    return null;
  }
};

/**
 * Helper to normalize chart config from tool response
 */
const normalizeChartConfig = (chart: any): any => {
  if (chart.config && typeof chart.config === 'object') {
    return {
      chart: {
        type: chart.chart_type || chart.config.chart?.type || 'line',
      },
      ...chart.config,
    };
  }
  return chart;
};

/**
 * Extract content blocks (charts, scripts, etc.) from tool invocations
 */
const extractContentBlocks = (tools: ToolInvocationData[]): ContentBlock[] => {
  const blocks: ContentBlock[] = [];

  for (const tool of tools) {
    if (!tool.tool_response_payload || tool.status !== 'success') continue;

    try {
      const payload = JSON.parse(tool.tool_response_payload);

      // Extract chart data
      if (payload.chart && typeof payload.chart === 'object') {
        blocks.push({
          type: 'charts',
          data: {
            type: 'highchart' as const,
            config: {
              ...normalizeChartConfig(payload.chart),
              credits: { enabled: false },
            },
          },
        });
      }

      // Extract script data
      if (payload.script && typeof payload.script === 'string') {
        blocks.push({
          type: 'code',
          data: {
            script: payload.script,
            language: payload.language || 'ruby',
            description: payload.description || '',
          },
        });
      }

      // Extract drain detection data
      if (payload.detection_id && payload.image_captions) {
        blocks.push({
          type: 'drain_detection',
          data: payload,
        });
      }
    } catch (e) {
      console.warn('Failed to parse tool response payload:', e);
    }
  }

  return blocks;
};

/**
 * Convert snake_case tool name to human-readable format
 * e.g., "query_assets" -> "Query Assets"
 */
const humanizeToolName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Build tool checklist from tool invocations
 */
const buildToolChecklist = (tools: ToolInvocationData[]): ToolInChecklist[] => {
  return tools.map(tool => {
    let description: string | undefined;
    if (tool.tool_response_payload && tool.status === 'success') {
      try {
        const payload = JSON.parse(tool.tool_response_payload);
        description = payload?.description ?? undefined;
      } catch {
        /* ignore parse errors */
      }
    }
    return {
      name: humanizeToolName(tool.tool_name),
      callId: tool.tool_call_id || `tool-${tool.invocation_id}`,
      status: tool.status === 'success' ? 'completed' : 'error',
      description,
    };
  });
};

/**
 * Parse reasoning content JSON and build ReasoningStep array
 */
const parseReasoningContent = (
  reasoningContent: string | null,
  tools: ToolInvocationData[],
): ReasoningStep[] => {
  const steps: ReasoningStep[] = [];
  const toolChecklist = buildToolChecklist(tools);

  // Parse reasoning content JSON array
  if (reasoningContent) {
    try {
      const reasoningArray = JSON.parse(reasoningContent);
      if (Array.isArray(reasoningArray)) {
        reasoningArray.forEach((content, index) => {
          steps.push({
            content,
            type: 'reasoning',
            // Attach tools to the first reasoning step
            tools: index === 0 ? toolChecklist : undefined,
          });
        });
      }
    } catch (e) {
      console.warn('Failed to parse reasoning content:', e);
    }
  }

  // If no reasoning content but tools were used, create a step with just tools
  if (steps.length === 0 && toolChecklist.length > 0) {
    steps.push({
      type: 'tool_log',
      tools: toolChecklist,
    });
  }

  return steps;
};

/**
 * Transform API response to ChatMessage array for rendering
 */
export const transformToMessages = (
  response: ConversationMessagesResponse,
): ChatMessage[] => {
  const chatMessages: ChatMessage[] = [];

  for (const msg of response.messages) {
    // Add user message
    chatMessages.push({
      role: 'user',
      content: msg.initial_prompt,
      timestamp: msg.requested_at || new Date().toISOString(),
      request_id: msg.request_id,
      messageCompleted: true,
      isRestored: true,
    });

    // Add assistant message if response exists
    if (msg.final_response || msg.tools.length > 0) {
      const contentBlocks = extractContentBlocks(msg.tools);
      const reasoningSteps = parseReasoningContent(
        msg.reasoning_content,
        msg.tools,
      );

      let restoredDurationSec: number | null = null;
      if (msg.requested_at && msg.completed_at) {
        const totalMs =
          new Date(msg.completed_at).getTime() -
          new Date(msg.requested_at).getTime();
        const toolMs = msg.tools.reduce(
          (sum, t) => sum + (t.processing_time_ms || 0),
          0,
        );
        restoredDurationSec = Math.max(
          1,
          Math.round((totalMs - toolMs) / 1000),
        );
      }

      // Parse transparency summary for restored messages
      let transparencySummary: ChatMessage['transparencySummary'] = undefined;
      if (msg.transparency_summary) {
        try {
          const parsed = JSON.parse(msg.transparency_summary);
          transparencySummary = {
            whatIDid: parsed.what_i_did || '',
            assumptions: parsed.assumptions || '',
            nextPrompts: Array.isArray(parsed.next_prompts)
              ? parsed.next_prompts.filter(
                  (prompt: unknown): prompt is string =>
                    typeof prompt === 'string' && prompt.trim().length > 0,
                )
              : [],
          };
        } catch {
          /* ignore parse errors */
        }
      }

      // Add text block if there's a final response
      if (msg.final_response) {
        contentBlocks.push({
          type: 'text',
          data: msg.final_response,
        });
      }

      chatMessages.push({
        role: 'assistant',
        content: msg.final_response || '',
        timestamp:
          msg.completed_at || msg.requested_at || new Date().toISOString(),
        request_id: msg.request_id,
        blocks: contentBlocks.length > 0 ? contentBlocks : undefined,
        reasoningSteps: reasoningSteps.length > 0 ? reasoningSteps : undefined,
        messageCompleted: true,
        isRestored: true,
        restoredDurationSec,
        transparencySummary,
        existingFeedback: msg.feedback
          ? {
              feedback_type: msg.feedback.feedback_type as
                | 'positive'
                | 'negative',
              comment: msg.feedback.comment,
              reason: msg.feedback.reason,
            }
          : null,
      });
    }
  }

  return chatMessages;
};
